<template>
  <VCard class="faq-form pa-0 pa-md-6 mt-6 grey lighten-5" elevation="0">
    <div class="px-0 px-md-4">
      <div class="step-title grey lighten-5 mb-n3 mb-md-2 pt-4 pt-md-0">
        {{ $t('stepTitle') }}
      </div>
      <div class="step-subtitle">
        {{ $t('commonQuestionsTip') }}
      </div>
    </div>
    <VCardText class="pt-0 pt-md-4">
      <VRow class="flex-md-row-reverse" wrap justify="end">
        <VCol v-if="hasAvailableFaq" cols="12" md="6" class="pl-md-8 mb-3 mb-md-0">
          <p class="subtitle-2">
            {{ $t('suggestedQuestions') }}
          </p>
          <p v-for="faq of visibleCommonFaq" :key="faq.id">
            <VIcon small class="mr-1 d-none d-md-inline-block" color="black">
              mdi-arrow-left-bold-outline
            </VIcon>
            <span class="pseudo-link body-2" @click="selectFaq(faq)">{{ faq.question }}</span>
          </p>
          <span
            v-if="canShowMoreCommonFaq"
            class="pseudo-link primary--text body-2"
            @click="showMoreCommonFaq"
          >
            {{ $t('showMore') }}
          </span>
        </VCol>
        <VCol cols="12" md="6">
          <VTextField
            v-model="question"
            outlined
            dense
            type="text"
            autocomplete="off"
            :error-messages="errors.question"
          >
            <template v-slot:label>
              {{ $t('question') }} <span>*</span>
            </template>
          </VTextField>
          <VTextarea
            v-model="answer"
            outlined
            :error-messages="errors.answer"
          >
            <template v-slot:label>
              {{ $t('answer') }} <span>*</span>
            </template>
          </VTextarea>
          <VBtn
            dark
            color="blue-grey"
            elevation="0"
            :loading="loading"
            @click="storeFaq"
          >
            {{ $t('addQuestion') }}
          </VBtn>
        </VCol>
      </VRow>

      <p v-if="hasFaq" class="subtitle-1">
        {{ $t('addedQuestions') }}
      </p>
      <VacancyFaqList :vacancy="vacancy" />
    </VCardText>
  </VCard>
</template>

<script>
import VacancyFaqList from './VacancyFaqList.vue';
import ValidationMixin from '../../mixins/ValidationMixin';
import VacancyFaq from '../../orm/models/Vacancy/VacancyFaq';

export default {
  name: 'VacancyFaqForm',
  components: {
    VacancyFaqList,
  },
  mixins: [ValidationMixin],
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      default: null,
      validator(value) {
        return value === null || typeof value === 'string';
      },
    },
    vacancy: {
      type: Object,
      required: true,
    },
    commonFaq: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      hiddenCommonFaqIds: [],
      commonFaqBegin: 0,
      commonFaqVisibleCount: 5,
      answer: null,
    };
  },
  computed: {
    question: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    availableCommonFaq() {
      return this.commonFaq.filter((f) => !this.hiddenCommonFaqIds.includes(f.id));
    },
    hasAvailableFaq() {
      return this.availableCommonFaq.length > 0;
    },
    canShowMoreCommonFaq() {
      return this.availableCommonFaq.length > this.commonFaqVisibleCount;
    },
    visibleCommonFaq() {
      const end = this.commonFaqBegin + this.commonFaqVisibleCount;
      const availableLength = this.availableCommonFaq.length;

      if (this.commonFaqVisibleCount > availableLength) {
        return this.availableCommonFaq;
      }

      if (end >= availableLength) {
        return [
          ...this.availableCommonFaq.slice(this.commonFaqBegin),
          ...this.availableCommonFaq.slice(0, end - availableLength),
        ];
      }

      return this.availableCommonFaq.slice(this.commonFaqBegin, end);
    },
    hasFaq() {
      return this.vacancy.faq && this.vacancy.faq.length > 0;
    },
  },
  methods: {
    selectFaq(faq) {
      this.hiddenCommonFaqIds.push(faq.id);
      this.question = faq.question;
      this.answer = faq.answer;
    },
    showMoreCommonFaq() {
      const end = this.commonFaqBegin + this.commonFaqVisibleCount;
      const availableLength = this.availableCommonFaq.length;

      if (this.commonFaqBegin === availableLength) {
        this.commonFaqBegin = 0;
      } else if (end > availableLength) {
        this.commonFaqBegin = end - availableLength;
      } else {
        this.commonFaqBegin += this.commonFaqVisibleCount;
      }
    },
    async storeFaq() {
      this.withValidationHandling(async () => {
        await VacancyFaq.api().store(this.vacancy.id, {
          question: this.question,
          answer: this.answer,
        });
        this.question = null;
        this.answer = null;
      });
    },
  },
};
</script>

<style lang="scss">
.faq-form .v-input__slot {
  background: white !important;
}
</style>

<i18n locale="en" lang="yaml">
  suggestedQuestions: Suggested questions
  showMore: Show more
  question: Question
  answer: Answer
  addQuestion: Add question
  addedQuestions: Added questions
  stepTitle: Answer frequently asked questions
  commonQuestionsTip: |-
    The format of the virtual interview does not provide the applicant the ability to ask questions.
    You can give answers to common questions about the company, and the applicant will see them
    after completing the interview.
</i18n>

<i18n locale="ru" lang="yaml">
  suggestedQuestions: Предлагаемые вопросы
  showMore: Показать еще
  question: Вопрос
  answer: Ответ
  addQuestion: Добавить вопрос
  addedQuestions: Добавленные вопросы
  stepTitle: Добавьте ответы на часто задаваемые вопросы
  commonQuestionsTip: |-
    Формат виртуального собеседования не подразумевает возможности соискателя задавать вопросы.
    Вы можете дать ответы на типовые вопросы о компании.
    Соискатель увидит их после прохождения интервью.
</i18n>
